import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Clock, Plus, Trash2, Mail, Calendar, Loader2 } from "lucide-react";

const AVAILABLE_TABLES = [
  { value: 'claims', label: 'Claims' },
  { value: 'properties', label: 'Properties' },
  { value: 'placements', label: 'Placements' },
  { value: 'profiles', label: 'Users' },
  { value: 'user_roles', label: 'User Roles' },
  { value: 'claim_status_history', label: 'Claim Status History' },
  { value: 'notifications', label: 'Notifications' },
];

const TABLE_COLUMNS: Record<string, string[]> = {
  claims: ['claim_number', 'policyholder_name', 'status', 'claim_type', 'loss_date', 'created_at'],
  properties: ['name', 'address', 'city', 'state', 'bedrooms', 'bathrooms', 'rent_amount', 'status'],
  placements: ['client_id', 'landlord_id', 'property_id', 'start_date', 'end_date', 'status'],
  profiles: ['email', 'created_at'],
  user_roles: ['user_id', 'role', 'created_at'],
  claim_status_history: ['claim_id', 'old_status', 'new_status', 'created_at'],
  notifications: ['title', 'type', 'read', 'created_at'],
};

interface ScheduledReport {
  id: string;
  report_name: string;
  description: string | null;
  tables: string[];
  columns: Record<string, string[]>;
  schedule_type: string;
  day_of_week: number | null;
  day_of_month: number | null;
  time_of_day: string;
  recipient_emails: string[];
  is_active: boolean;
  last_sent_at: string | null;
  next_run_at: string | null;
  created_at: string;
}

export function ScheduledReportsManager() {
  const [schedules, setSchedules] = useState<ScheduledReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [reportName, setReportName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<Record<string, string[]>>({});
  const [scheduleType, setScheduleType] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState<number>(1); // Monday
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [recipientEmails, setRecipientEmails] = useState('');

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const { data, error } = await supabase
        .from('scheduled_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSchedules((data || []) as ScheduledReport[]);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      toast({ title: "Failed to load scheduled reports", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const toggleTable = (table: string) => {
    if (selectedTables.includes(table)) {
      setSelectedTables(prev => prev.filter(t => t !== table));
      setSelectedColumns(prev => {
        const updated = { ...prev };
        delete updated[table];
        return updated;
      });
    } else {
      setSelectedTables(prev => [...prev, table]);
      setSelectedColumns(prev => ({
        ...prev,
        [table]: TABLE_COLUMNS[table] || []
      }));
    }
  };

  const calculateNextRun = (type: string, dow?: number, dom?: number, time?: string): Date => {
    const now = new Date();
    const [hours, minutes] = (time || '09:00').split(':').map(Number);
    const nextRun = new Date();
    nextRun.setHours(hours, minutes, 0, 0);

    if (nextRun <= now) {
      nextRun.setDate(nextRun.getDate() + 1);
    }

    switch (type) {
      case 'weekly':
        const currentDay = nextRun.getDay();
        const targetDay = dow || 1;
        const daysUntil = (targetDay - currentDay + 7) % 7 || 7;
        nextRun.setDate(nextRun.getDate() + daysUntil);
        break;
      case 'monthly':
        nextRun.setDate(dom || 1);
        if (nextRun <= now) {
          nextRun.setMonth(nextRun.getMonth() + 1);
        }
        break;
    }

    return nextRun;
  };

  const handleCreate = async () => {
    if (!reportName.trim()) {
      toast({ title: "Please enter a report name", variant: "destructive" });
      return;
    }
    if (selectedTables.length === 0) {
      toast({ title: "Please select at least one table", variant: "destructive" });
      return;
    }
    if (!recipientEmails.trim()) {
      toast({ title: "Please enter at least one recipient email", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const emails = recipientEmails.split(',').map(e => e.trim()).filter(Boolean);
      const nextRun = calculateNextRun(scheduleType, dayOfWeek, dayOfMonth, timeOfDay);

      const { error } = await supabase.from('scheduled_reports').insert({
        user_id: user.id,
        report_name: reportName,
        description: description || null,
        tables: selectedTables,
        columns: selectedColumns,
        schedule_type: scheduleType,
        day_of_week: scheduleType === 'weekly' ? dayOfWeek : null,
        day_of_month: scheduleType === 'monthly' ? dayOfMonth : null,
        time_of_day: timeOfDay,
        recipient_emails: emails,
        next_run_at: nextRun.toISOString(),
      });

      if (error) throw error;

      toast({ title: "Scheduled report created successfully" });
      setDialogOpen(false);
      resetForm();
      fetchSchedules();
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast({ title: "Failed to create scheduled report", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('scheduled_reports')
        .update({ is_active: !isActive })
        .eq('id', id);

      if (error) throw error;
      
      toast({ title: isActive ? "Schedule paused" : "Schedule activated" });
      fetchSchedules();
    } catch (error) {
      console.error('Error toggling schedule:', error);
      toast({ title: "Failed to update schedule", variant: "destructive" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('scheduled_reports')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({ title: "Scheduled report deleted" });
      fetchSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast({ title: "Failed to delete schedule", variant: "destructive" });
    }
  };

  const resetForm = () => {
    setReportName('');
    setDescription('');
    setSelectedTables([]);
    setSelectedColumns({});
    setScheduleType('weekly');
    setDayOfWeek(1);
    setDayOfMonth(1);
    setTimeOfDay('09:00');
    setRecipientEmails('');
  };

  const getScheduleDescription = (schedule: ScheduledReport) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    switch (schedule.schedule_type) {
      case 'daily':
        return `Daily at ${schedule.time_of_day}`;
      case 'weekly':
        return `Every ${days[schedule.day_of_week || 0]} at ${schedule.time_of_day}`;
      case 'monthly':
        return `Monthly on day ${schedule.day_of_month} at ${schedule.time_of_day}`;
      default:
        return schedule.schedule_type;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Scheduled Reports
            </CardTitle>
            <CardDescription>
              Automatically send reports to team members on a recurring schedule
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                New Schedule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Scheduled Report</DialogTitle>
                <DialogDescription>
                  Set up a recurring report to be emailed automatically
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Report Name</Label>
                  <Input
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    placeholder="Weekly Claims Summary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of this report"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Select Tables</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_TABLES.map((table) => (
                      <div key={table.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`schedule-${table.value}`}
                          checked={selectedTables.includes(table.value)}
                          onCheckedChange={() => toggleTable(table.value)}
                        />
                        <label htmlFor={`schedule-${table.value}`} className="text-sm">
                          {table.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select value={scheduleType} onValueChange={(v: any) => setScheduleType(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {scheduleType === 'weekly' && (
                    <div className="space-y-2">
                      <Label>Day of Week</Label>
                      <Select value={String(dayOfWeek)} onValueChange={(v) => setDayOfWeek(Number(v))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Sunday</SelectItem>
                          <SelectItem value="1">Monday</SelectItem>
                          <SelectItem value="2">Tuesday</SelectItem>
                          <SelectItem value="3">Wednesday</SelectItem>
                          <SelectItem value="4">Thursday</SelectItem>
                          <SelectItem value="5">Friday</SelectItem>
                          <SelectItem value="6">Saturday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {scheduleType === 'monthly' && (
                    <div className="space-y-2">
                      <Label>Day of Month</Label>
                      <Input
                        type="number"
                        min={1}
                        max={28}
                        value={dayOfMonth}
                        onChange={(e) => setDayOfMonth(Number(e.target.value))}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Time of Day</Label>
                  <Input
                    type="time"
                    value={timeOfDay}
                    onChange={(e) => setTimeOfDay(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Recipient Emails (comma-separated)</Label>
                  <Input
                    value={recipientEmails}
                    onChange={(e) => setRecipientEmails(e.target.value)}
                    placeholder="admin@example.com, manager@example.com"
                  />
                </div>

                <Button onClick={handleCreate} disabled={saving} className="w-full">
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Calendar className="w-4 h-4 mr-2" />
                  )}
                  Create Schedule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : schedules.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No scheduled reports yet. Create one to automatically send reports.
          </p>
        ) : (
          <div className="space-y-4">
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium">{schedule.report_name}</h4>
                    <Badge variant={schedule.is_active ? "default" : "secondary"}>
                      {schedule.is_active ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getScheduleDescription(schedule)}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Mail className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {schedule.recipient_emails.join(', ')}
                    </span>
                  </div>
                  {schedule.next_run_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Next run: {format(new Date(schedule.next_run_at), 'PPp')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={schedule.is_active}
                    onCheckedChange={() => handleToggleActive(schedule.id, schedule.is_active)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(schedule.id)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}